import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const AdrmController = ({
  data,
  setDataForTable,
  setFilteredData,
  openStates,
  setSelectedData,
  setOpenStates,
}) => {
  // State for search inputs
  const [searchValues, setSearchValues] = useState({
    MONTH: "",
    BLOCK_DATE: "",
    DEPT: "",
    "DATA MATRIX": "",
  });

  // State for selected filters
  const [selectedMonth, setSelectedMonth] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(new Set());
  const [selectedDept, setSelectedDept] = useState(new Set());
  const [selectedMatrixField, setSelectedMatrixField] = useState(""); // Changed to string for single selection

  // Available matrix fields
  const matrixFields = ["Avg per day", "Avg per month", "Without Avg"];

  // Static list of months
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Map month names to numeric format (e.g., "Apr" -> "04")
  const monthToNumber = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  // Update table data based on selected filters
  useEffect(() => {
    setDataForTable({
      month: Array.from(selectedMonth),
      date: Array.from(selectedDate),
      dept: Array.from(selectedDept),
      matrixField: selectedMatrixField ? [selectedMatrixField] : [], // Convert to array for consistency
    });
  }, [
    selectedMonth,
    selectedDate,
    selectedDept,
    selectedMatrixField,
    setDataForTable,
  ]);

  // Toggle selection for checkboxes (for MONTH, BLOCK_DATE, DEPT)
  const toggleSelection = (setFunc, currentSet, value) => {
    const newSet = new Set(currentSet);
    newSet.has(value) ? newSet.delete(value) : newSet.add(value);
    setFunc(newSet);
  };

  // Toggle selection for DATA MATRIX (single selection)
  const toggleMatrixFieldSelection = (value) => {
    setSelectedMatrixField(selectedMatrixField === value ? "" : value); // Deselect if already selected, else select new value
  };

  // Toggle dropdown visibility
  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Handle search input changes
  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  // Initialize filter options
  const defaultSetup = () => {
    if (!data?.currentData?.length && !data?.previousData?.length) return;
    const allData = [...(data.currentData || []), ...(data.previousData || [])];
    const allDates = [
      ...new Set(
        allData.map((d) => d.block_date?.slice(0, 10)).filter((v) => v != null)
      ),
    ];
    const allDepts = [
      ...new Set(allData.map((d) => d.dept).filter((v) => v != null)),
    ];

    setSelectedMonth(new Set(monthNames));
    setSelectedDate(new Set(allDates));
    setSelectedDept(new Set(allDepts));
    setSelectedMatrixField("Without Avg"); // Default to first matrix field or empty string
    setSearchValues({
      MONTH: "",
      BLOCK_DATE: "",
      DEPT: "",
      "DATA MATRIX": "",
    });
    setOpenStates({
      MONTH: false,
      BLOCK_DATE: false,
      DEPT: false,
      "DATA MATRIX": false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  // Available options for each filter
  const availableMonths = useMemo(() => {
    return monthNames.filter((month) =>
      [...(data.currentData || []), ...(data.previousData || [])].some(
        (entry) => entry.block_date?.slice(5, 7) === monthToNumber[month]
      )
    );
  }, [data]);

  const availableDates = useMemo(() => {
    return [
      ...new Set(
        [...(data.currentData || []), ...(data.previousData || [])]
          .filter((entry) =>
            Array.from(selectedMonth).some(
              (month) => entry.block_date?.slice(5, 7) === monthToNumber[month]
            )
          )
          .map((d) => d.block_date?.slice(0, 10))
          .filter((v) => v != null)
      ),
    ].sort();
  }, [data, selectedMonth]);

  const availableDepts = useMemo(() => {
    return [
      ...new Set(
        [...(data.currentData || []), ...(data.previousData || [])]
          .filter(
            (entry) =>
              Array.from(selectedMonth).some(
                (month) =>
                  entry.block_date?.slice(5, 7) === monthToNumber[month]
              ) && selectedDate.has(entry.block_date?.slice(0, 10))
          )
          .map((d) => d.dept.toUpperCase())
          .filter((v) => v != null)
      ),
    ].sort();
  }, [data, selectedMonth, selectedDate]);

  const availableMatrixFields = useMemo(() => {
    return matrixFields;
  }, [data, selectedMonth, selectedDate, selectedDept]);

  // Filtered data and matrix calculation
  const filteredData = useMemo(() => {
    if (
      !data ||
      (!data.currentData?.length && !data.previousData?.length) ||
      selectedMonth.size === 0 ||
      selectedDate.size === 0 ||
      selectedDept.size === 0 ||
      !selectedMatrixField // Require a matrix field to be selected
    ) {
      return {
        currentData: [],
        previousData: [],
        matrix: {},
      };
    }
    const filterData = (dataArray) =>
      dataArray?.filter((entry) => {
        return (
          Array.from(selectedMonth).some(
            (month) => entry.block_date?.slice(5, 7) === monthToNumber[month]
          ) &&
          selectedDate.has(entry.block_date?.slice(0, 10)) &&
          selectedDept.has(entry.dept)
        );
      }) || [];

    const currentFiltered = filterData(data.currentData);
    const previousFiltered = filterData(data.previousData);

    // Calculate matrix for selected field
    const matrix = selectedMatrixField
      ? {
          [selectedMatrixField]: availableDepts.reduce((acc, dept) => {
            acc[dept] = availableDates.reduce((dateAcc, date) => {
              const sum = [...currentFiltered, ...previousFiltered]
                .filter(
                  (entry) =>
                    entry.dept === dept &&
                    entry.block_date?.slice(0, 10) === date
                )
                .reduce(
                  (sum, entry) => sum + (entry[selectedMatrixField] || 0),
                  0
                );
              dateAcc[date] = sum;
              return dateAcc;
            }, {});
            return acc;
          }, {}),
        }
      : {};

    return {
      currentData: currentFiltered,
      previousData: previousFiltered,
      matrix,
    };
  }, [
    data,
    selectedMonth,
    selectedDate,
    selectedDept,
    selectedMatrixField,
    availableDepts,
    availableDates,
  ]);

  // Propagate filteredData to parent component
  useEffect(() => {
    setFilteredData(filteredData);
    setSelectedData({ selectedDept, selectedMatrixField });
  }, [filteredData, setFilteredData]);

  // Render checkbox list for each filter
  const renderCheckboxList = (title, items, selectedValue, setFunc) => {
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        ?.toLowerCase()
        ?.includes(searchValues[title]?.toLowerCase() || "")
    );

    const isMatrixField = title === "DATA MATRIX";

    return (
      <div className="col-md mt-2 p-1">
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="papaDiv"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title} {"  "}
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="papaDiv"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title] || ""}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="col-sx fade-in"
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type={isMatrixField ? "radio" : "checkbox"}
                          checked={
                            isMatrixField
                              ? selectedValue === item
                              : selectedValue.has(item)
                          }
                          onChange={() =>
                            isMatrixField
                              ? toggleMatrixFieldSelection(item)
                              : toggleSelection(setFunc, selectedValue, item)
                          }
                        />{" "}
                        {item || "N/A"}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              <div className="d-flex justify-content-evenly mt-1">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() =>
                    isMatrixField
                      ? setFunc(filteredItems[0] || "")
                      : setFunc(new Set(filteredItems))
                  }
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() =>
                    isMatrixField ? setFunc("") : setFunc(new Set())
                  }
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="papaDiv">
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        {renderCheckboxList(
          "MONTH",
          availableMonths,
          selectedMonth,
          setSelectedMonth
        )}
        {renderCheckboxList(
          "BLOCK_DATE",
          availableDates,
          selectedDate,
          setSelectedDate
        )}
        {renderCheckboxList(
          "DEPT",
          availableDepts,
          selectedDept,
          setSelectedDept
        )}
        {renderCheckboxList(
          "DATA MATRIX",
          availableMatrixFields,
          selectedMatrixField,
          setSelectedMatrixField
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-reset">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 3px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default AdrmController;
